// FastAPI backend service - dynamically connects to any backend endpoint
// Used when REACT_APP_USE_STATIC_DATA=false

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  };

  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      let errorDetails = null;

      try {
        const errorData = await response.json();
        errorDetails = errorData;
        if (response.status === 422 && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            const fieldErrors = errorData.detail.map(err => `${err.loc?.join('.') || 'field'}: ${err.msg}`).join(', ');
            errorMessage = `Validation Error: ${fieldErrors}`;
          } else {
            errorMessage = `Validation Error: ${errorData.detail}`;
          }
        } else {
          errorMessage = errorData.detail || errorData.message || errorMessage;
        }
      } catch (e) {
        errorMessage = response.statusText || errorMessage;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.details = errorDetails;
      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`FastAPI request failed for ${endpoint}:`, error);
    throw error;
  }
};

const cleanDataForCreate = (data) => {
  const cleaned = { ...data };
  delete cleaned.id;
  delete cleaned.created_at;
  delete cleaned.updated_at;
  Object.keys(cleaned).forEach(key => {
    if (key.endsWith('_created_at') || key.endsWith('_updated_at')) {
      delete cleaned[key];
    }
    if (cleaned[key] === undefined || cleaned[key] === null || cleaned[key] === '') {
      delete cleaned[key];
    }
  });
  return cleaned;
};

const cleanDataForUpdate = (data) => {
  const cleaned = { ...data };
  delete cleaned.created_at;
  delete cleaned.updated_at;
  Object.keys(cleaned).forEach(key => {
    if (key.endsWith('_created_at') || key.endsWith('_updated_at')) {
      delete cleaned[key];
    }
    if (cleaned[key] === undefined || cleaned[key] === null || cleaned[key] === '') {
      delete cleaned[key];
    }
  });
  return cleaned;
};

export const fastApiService = {
  getAll: (entity, params = {}) => {
    const searchParams = new URLSearchParams(params);
    return apiRequest(`/${entity}?${searchParams}`);
  },
  getById: (entity, id) => apiRequest(`/${entity}/${id}`),
  create: (entity, data) => {
    const cleanData = cleanDataForCreate(data);
    return apiRequest(`/${entity}`, { method: 'POST', body: JSON.stringify(cleanData) });
  },
  update: (entity, id, data) => {
    const cleanData = cleanDataForUpdate(data);
    return apiRequest(`/${entity}/${id}`, { method: 'PATCH', body: JSON.stringify(cleanData) });
  },
  delete: (entity, id) => apiRequest(`/${entity}/${id}`, { method: 'DELETE' }),
  search: (entity, query, params = {}) => {
    const searchParams = new URLSearchParams({ q: query, ...params });
    return apiRequest(`/${entity}/search?${searchParams}`);
  },
  login: (credentials) => apiRequest('/auth/login', { method: 'POST', body: JSON.stringify(credentials) }),
  logout: () => apiRequest('/auth/logout', { method: 'POST' }),
  healthCheck: () => apiRequest('/health'),
};
